import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { chamarApi, type ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';
import type { StatusVariant } from '@/ui/StatusBadge';

import type { ItemDoChecklist, SituacaoDoChecklist } from './contrato';
import { alertaDeValidade } from './vencimento';

/**
 * Frota e equipe do credenciado, da API real.
 *
 * Lê `GET /bff/v1/j05-s01-cadastrar-{veiculo,monitor}/state`
 * (`apps/api/src/modulos/recursos-credenciado`), que devolve a função
 * `j05s01_estado` (`db/migrations/0063`). Quem decide de quem é o recurso é o
 * banco, pelo usuário do token: a tela não manda identificador nenhum, nem o
 * cabeçalho `X-Credenciado-Id` — sem seletor de cadastro no front, a API usa
 * o cadastro padrão da pessoa (mesma escolha de `inscricao/dados.ts`).
 *
 * A resposta é curta: por recurso, o identificador (placa ou CPF), o nome
 * (marca e modelo, no veículo), o estado do cadastro e as versões de
 * documento. O resto do que as telas do protótipo mostram a API ainda não
 * devolve, e cada tela diz "—" nesses campos em vez de inventar.
 *
 * A API também não pagina: devolve a coleção inteira do cadastro. As telas
 * filtram e paginam no `select` do React Query, sobre a mesma consulta.
 */
export type TipoDeRecurso = 'veiculo' | 'monitor';

export function caminhoDosRecursos(tipo: TipoDeRecurso): string {
  return `/bff/v1/j05-s01-cadastrar-${tipo}/state`;
}

/** `estado_cadastro` de veículos, condutores e monitores (0063). */
export const ESTADOS_DO_CADASTRO = ['EM_ANALISE', 'APROVADO', 'REPROVADO', 'LEGADO'] as const;
export type EstadoDoCadastro = (typeof ESTADOS_DO_CADASTRO)[number];

/**
 * Rótulo e selo de cada estado. `LEGADO` é o recurso que entrou no sistema sem
 * passar pela análise do portal (valor padrão da coluna para o que já existia).
 */
export const ESTADO_DO_CADASTRO: Record<EstadoDoCadastro, { rotulo: string; selo: StatusVariant }> =
  {
    EM_ANALISE: { rotulo: 'Em análise', selo: 'pendente' },
    APROVADO: { rotulo: 'Aprovado', selo: 'aprovado' },
    REPROVADO: { rotulo: 'Reprovado', selo: 'rejeitado' },
    LEGADO: { rotulo: 'Cadastro legado', selo: 'inativo' },
  };

export const OPCOES_DE_ESTADO_DO_CADASTRO: readonly { value: EstadoDoCadastro; label: string }[] =
  ESTADOS_DO_CADASTRO.map((estado) => ({
    value: estado,
    label: ESTADO_DO_CADASTRO[estado].rotulo,
  }));

export function estadoDoCadastroValido(valor: string): EstadoDoCadastro | undefined {
  return (ESTADOS_DO_CADASTRO as readonly string[]).includes(valor)
    ? (valor as EstadoDoCadastro)
    : undefined;
}

/** Uma versão de documento, como `j05s01_estado` devolve. */
export interface DocumentoDoRecursoDaApi {
  id: string;
  versao: number;
  /** O requisito atendido (ex.: "CRLV"). */
  tipo: string;
  /**
   * O nome do requisito no catálogo do edital, pela natureza do cadastro.
   * `null` quando o tipo não é do catálogo (cadastro com tipo livre).
   */
  titulo?: string | null;
  /**
   * Validade aprovada (AAAA-MM-DD), da decisão que aprovou o documento.
   * `null` enquanto ele não foi aprovado.
   */
  validade?: string | null;
  /** Nome do arquivo enviado. */
  nome: string;
  hash: string;
  status: 'EM_ANALISE' | 'VALIDO' | 'RECUSADO' | 'SUBSTITUIDO';
}

/** Um recurso, como `j05s01_estado` devolve. */
export interface RecursoDaApi {
  id: string;
  /** Placa do veículo; CPF do monitor, sem máscara. */
  identificador: string;
  /** Nome do monitor; marca e modelo do veículo, separados por espaço. Vazio quando não há. */
  nome: string | null;
  estado: EstadoDoCadastro;
  /** `null` quando o recurso não tem documento (é o `jsonb_agg` de nada). */
  documentos: DocumentoDoRecursoDaApi[] | null;
}

export interface RespostaDosRecursos {
  state: { recursos: RecursoDaApi[] };
}

const SITUACAO_DO_DOCUMENTO: Record<
  Exclude<DocumentoDoRecursoDaApi['status'], 'SUBSTITUIDO'>,
  SituacaoDoChecklist
> = {
  VALIDO: 'aprovado',
  EM_ANALISE: 'enviado',
  RECUSADO: 'rejeitado',
};

/**
 * Uma linha do checklist por requisito, com a versão mais recente dele. A API
 * não diz quais requisitos são obrigatórios: nenhum item sai marcado como
 * obrigatório. A validade vem da aprovação, e o selo de vencendo e vencido é o
 * do protótipo; documento aprovado que já venceu sai como `vencido`.
 *
 * O nome da linha é o título do requisito; sem título, o tipo como veio. O
 * código do requisito (`CRM_VEICULO`) não é nome para o transportador ler.
 */
export function documentosDoChecklist(
  documentos: DocumentoDoRecursoDaApi[] | null,
): ItemDoChecklist[] {
  return versoesEmVigor(documentos).map((documento) => {
    const validade = documento.validade ?? null;
    const vencido = documento.status === 'VALIDO' && alertaDeValidade(validade) === 'vencido';
    return {
      id: documento.id,
      nome: textoOuNulo(documento.titulo) ?? documento.tipo,
      obrigatorio: false,
      situacao: vencido
        ? 'vencido'
        : SITUACAO_DO_DOCUMENTO[documento.status as keyof typeof SITUACAO_DO_DOCUMENTO],
      documento: { arquivoNome: documento.nome, validadeAte: validade },
    };
  });
}

/** A versão mais recente de cada requisito, sem as substituídas. */
function versoesEmVigor(documentos: DocumentoDoRecursoDaApi[] | null): DocumentoDoRecursoDaApi[] {
  const maisRecente = new Map<string, DocumentoDoRecursoDaApi>();
  for (const documento of documentos ?? []) {
    if (documento.status === 'SUBSTITUIDO') {
      continue;
    }
    const atual = maisRecente.get(documento.tipo);
    if (!atual || documento.versao > atual.versao) {
      maisRecente.set(documento.tipo, documento);
    }
  }
  return [...maisRecente.values()];
}

/**
 * Validade aprovada do documento de um requisito (ex.: `CRLV`), da versão em
 * vigor. `null` quando não há documento aprovado desse requisito.
 */
export function validadeDoRequisito(
  documentos: DocumentoDoRecursoDaApi[] | null,
  requisito: string,
): string | null {
  const documento = versoesEmVigor(documentos).find(
    (d) => d.tipo === requisito && d.status === 'VALIDO',
  );
  return documento?.validade ?? null;
}

/** Texto não vazio, ou `null`: a API devolve `''` para marca e modelo em branco. */
export function textoOuNulo(valor: string | null | undefined): string | null {
  const limpo = valor?.trim();
  return limpo ? limpo : null;
}

export const CHAVES_DOS_RECURSOS = {
  todos: (tipo: TipoDeRecurso) => ['credenciado', 'recursos', tipo] as const,
};

/**
 * Os recursos de um tipo, convertidos para o formato da tela e passados pelo
 * `selecionar` — filtro e página na lista, busca por id no detalhe. Lista e
 * detalhe dividem a mesma consulta: abrir o detalhe a partir da lista não
 * busca de novo.
 */
export function useRecursosDoCredenciado<Recurso, Resultado>(
  tipo: TipoDeRecurso,
  converter: (recurso: RecursoDaApi) => Recurso,
  selecionar: (recursos: Recurso[]) => Resultado,
  habilitado = true,
): UseQueryResult<Resultado, ErroApi> {
  return useQuery<RespostaDosRecursos, ErroApi, Resultado>({
    queryKey: CHAVES_DOS_RECURSOS.todos(tipo),
    queryFn: ({ signal }) =>
      chamarApi<RespostaDosRecursos>(caminhoDosRecursos(tipo), { sinal: signal }),
    select: (resposta) => selecionar((resposta.state.recursos ?? []).map(converter)),
    retry: naoInsistirEmRecusa,
    enabled: habilitado,
  });
}
